import { type Material, Mesh, type Object3D } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/**
 * Collapse many static meshes that share a material into one merged mesh each —
 * turning hundreds of per-ramp draw calls (the 300–500-shape arcane courses) into
 * one per material. World transforms are baked into the merged geometry, so the
 * result sits at the origin.
 *
 * The merged meshes are used for BOTH render and collision: `LevelCollider`
 * re-merges whatever it's given into a single BVH, so 3 big meshes cost the same
 * to query as 500 small ones — but render far cheaper. The originals are removed
 * from `parent` and their geometry disposed. Returns the merged meshes (the new
 * collider set). Editor/gizmo flows keep the unmerged meshes — don't call this
 * where individual placements must stay selectable.
 */
export function mergeStaticMeshes(parent: Object3D, meshes: Mesh[]): Mesh[] {
  const byMaterial = new Map<Material, Mesh[]>();
  for (const m of meshes) {
    const mat = m.material as Material;
    const group = byMaterial.get(mat);
    if (group) {
      group.push(m);
    } else {
      byMaterial.set(mat, [m]);
    }
  }

  const out: Mesh[] = [];
  for (const [material, group] of byMaterial) {
    const baked = group.map((m) => {
      m.updateMatrixWorld(true);
      const g = m.geometry.index ? m.geometry.toNonIndexed() : m.geometry.clone();
      g.applyMatrix4(m.matrixWorld);
      // Keep only the attributes every ramp geometry shares, so the merge is valid.
      for (const name of Object.keys(g.attributes)) {
        if (name !== 'position' && name !== 'normal' && name !== 'uv') {
          g.deleteAttribute(name);
        }
      }
      return g;
    });

    const merged = mergeGeometries(baked, false);
    for (const g of baked) {
      g.dispose();
    }

    if (!merged) {
      // Incompatible attributes — leave this group untouched (still rendered/collided).
      out.push(...group);
      continue;
    }

    for (const m of group) {
      parent.remove(m);
      m.geometry.dispose();
    }
    const mesh = new Mesh(merged, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    parent.add(mesh);
    out.push(mesh);
  }
  return out;
}
